import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/toast';

// Define the GraphQL query
const LIST_BILLING_EVENTS = gql`
  query ListBillingEvents($startDate: String, $endDate: String) {
    listBillingEvents(startDate: $startDate, endDate: $endDate) {
      cin
      orchidMrun
      mbrLastname
      mbrFirstname
      mbrMiddlename
      mbrDob
      mbrGender
      cbCmeId
      cbcmeOrgName
      billingProvStAddress1
      billingProvStAddress2
      billingProvCity
      billingProvState
      billingProvZip
      billingProvTaxId
      billProvSpecInfo
      providerTin
      providerName
      providerPhone
      providerStreetAddress
      providerCity
      providerState
      providerZip
      memberHomelessStatus
      memberResidentialAddress1
      memberResidentialAddress2
      memberResidentialCity
      memberResidentialState
      memberResidentialZip
      mcpIdentifier
      mcpName
      procCodeQualifier
      procedureCode
      procedureModifier1
      procedureModifier2
      serviceStartDate
      serviceEndDate
      serviceUnitCount
      posCode
      diagCodeQualifier
      diagnosisCode1
      diagnosisCode2
      diagnosisCode3
      diagnosisCode4
      diagnosisCode5
      diagnosisCode6
      diagnosisCode7
      diagnosisCode8
      diagnosisCode9
      diagnosisCode10
      serviceUnitCost
      serviceChargeAmounts
      invoiceAmount
      invoiceDate
      fin
      referralNumber
      claimFrequencyTypeCode
      releaseOfInfoCode
    }
  }
`;

interface BillingEvent {
  cin?: string;
  orchidMrun?: string;
  mbrLastname?: string;
  mbrFirstname?: string;
  mbrMiddlename?: string;
  mbrDob?: string;
  mbrGender?: string;
  cbCmeId?: string;
  cbcmeOrgName?: string;
  billingProvStAddress1?: string;
  billingProvStAddress2?: string;
  billingProvCity?: string;
  billingProvState?: string;
  billingProvZip?: string;
  billingProvTaxId?: string;
  billProvSpecInfo?: string;
  providerTin?: string;
  providerName?: string;
  providerPhone?: string;
  providerStreetAddress?: string;
  providerCity?: string;
  providerState?: string;
  providerZip?: string;
  memberHomelessStatus?: string;
  memberResidentialAddress1?: string;
  memberResidentialAddress2?: string;
  memberResidentialCity?: string;
  memberResidentialState?: string;
  memberResidentialZip?: string;
  mcpIdentifier?: string;
  mcpName?: string;
  procCodeQualifier?: string;
  procedureCode?: string;
  procedureModifier1?: string;
  procedureModifier2?: string;
  serviceStartDate?: string;
  serviceEndDate?: string;
  serviceUnitCount?: string;
  posCode?: string;
  diagCodeQualifier?: string;
  diagnosisCode1?: string;
  diagnosisCode2?: string;
  diagnosisCode3?: string;
  diagnosisCode4?: string;
  diagnosisCode5?: string;
  diagnosisCode6?: string;
  diagnosisCode7?: string;
  diagnosisCode8?: string;
  diagnosisCode9?: string;
  diagnosisCode10?: string;
  serviceUnitCost?: string;
  serviceChargeAmounts?: string;
  invoiceAmount?: string;
  invoiceDate?: string;
  fin?: string;
  referralNumber?: string;
  claimFrequencyTypeCode?: string;
  releaseOfInfoCode?: string;
}

interface Column {
  key: keyof BillingEvent;
  label: string;
}

export const GenerateBillingReport = () => {

    
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedBillingEvent, setSelectedBillingEvent] = useState<BillingEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Execute the lazy query
  const [executeQuery, { loading, error, data }] = useLazyQuery(LIST_BILLING_EVENTS);
  
  // Handle form submission
  const handleSubmit = () => {
    executeQuery({
      variables: {
        startDate: startDate || undefined,
        endDate: endDate || undefined
      }
    });
  };

  // Handle view billing event
  const handleViewBillingEvent = (billingEvent: BillingEvent) => {
    setSelectedBillingEvent(billingEvent);
    setIsModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBillingEvent(null);
  };

  // Format service unit count for display
  const formatServiceUnitCount = (serviceUnitCount: string | undefined): string => {
    if (!serviceUnitCount) return '-';
    return `${serviceUnitCount} units`;
  };

  // Format invoice amount - hard coded to 1
  const formatInvoiceAmount = (): string => {
    return '1';
  };

  // CSV Download function
  const downloadCSV = () => {
    if (!data?.listBillingEvents || data.listBillingEvents.length === 0) {
      toast.error('No data available to download');
      return;
    }

    // Define CSV headers as specified
    const headers = [
      'CIN', 'ORCHID_MRUN', 'MBR_LASTNAME', 'MBR_FIRSTNAME', 'MBR_MIDDLENAME', 
      'MBR_DOB', 'MBR_GENDER', 'CB_CME_ID', 'CBCME_ORG_NAME', 'BillingProvStAddress_1', 
      'BillingProvStAddress_2', 'BillingProvCity', 'BillingProvState', 'BillingProvZip', 
      'BillingProvTaxID', 'BILLPROVSPECINFO', 'PROVIDER_TIN', 'PROVIDER_NAME', 
      'PROVIDER_PHONE', 'PROVIDER_STREET_ADDRESS', 'PROVIDER_CITY', 'PROVIDER_STATE', 
      'PROVIDER_ZIP', 'MEMBER_HOMELESS_STATUS', 'MEMBER_RESIDENTIAL_ADDRESS_1', 
      'MEMBER_RESIDENTIAL_ADDRESS_2', 'MEMBER_RESIDENTIAL_CITY', 'MEMBER_RESIDENTIAL_STATE', 
      'MEMBER_RESIDENTIAL_ZIP', 'MCP_IDENTIFIER', 'MCP_NAME', 'PROC_CODE_QUALIFIER', 
      'PROCEDURE_CODE', 'PROCEDURE_MODIFIER_1', 'PROCEDURE_MODIFIER_2', 'SERVICE_START_DATE', 
      'SERVICE_END_DATE', 'SERVICE_UNIT_COUNT', 'POS_CODE', 'DIAG_CODE_QUALIFIER', 
      'DIAGNOSIS_CODE_1', 'DIAGNOSIS_CODE_2', 'DIAGNOSIS_CODE_3', 'DIAGNOSIS_CODE_4', 
      'DIAGNOSIS_CODE_5', 'DIAGNOSIS_CODE_6', 'DIAGNOSIS_CODE_7', 'DIAGNOSIS_CODE_8', 
      'DIAGNOSIS_CODE_9', 'DIAGNOSIS_CODE_10', 'SERVICE_UNIT_COST', 'SERVICE_CHARGE_AMOUNTS', 
      'INVOICE_AMOUNT', 'INVOICE_DATE', 'FIN', 'REFERRAL_NUMBER', 'CLAIM_FREQUENCY_TYPE_CODE', 
      'RELEASE_OF_INFO_CODE'
    ];

    // Map data to match headers
    const csvData: string[][] = data.listBillingEvents.map((item: BillingEvent) => [
      item.cin || '',
      item.orchidMrun || '',
      item.mbrLastname || '',
      item.mbrFirstname || '',
      item.mbrMiddlename || '',
      item.mbrDob || '',
      item.mbrGender || '',
      item.cbCmeId || '',
      item.cbcmeOrgName || '',
      item.billingProvStAddress1 || '',
      item.billingProvStAddress2 || '',
      item.billingProvCity || '',
      item.billingProvState || '',
      item.billingProvZip || '',
      item.billingProvTaxId || '',
      item.billProvSpecInfo || '',
      item.providerTin || '',
      item.providerName || '',
      item.providerPhone || '',
      item.providerStreetAddress || '',
      item.providerCity || '',
      item.providerState || '',
      item.providerZip || '',
      item.memberHomelessStatus || '',
      item.memberResidentialAddress1 || '',
      item.memberResidentialAddress2 || '',
      item.memberResidentialCity || '',
      item.memberResidentialState || '',
      item.memberResidentialZip || '',
      item.mcpIdentifier || '',
      item.mcpName || '',
      item.procCodeQualifier || '',
      item.procedureCode || '',
      item.procedureModifier1 || '',
      item.procedureModifier2 || '',
      item.serviceStartDate || '',
      item.serviceEndDate || '',
      item.serviceUnitCount || '',
      item.posCode || '',
      item.diagCodeQualifier || '',
      item.diagnosisCode1 || '',
      item.diagnosisCode2 || '',
      item.diagnosisCode3 || '',
      item.diagnosisCode4 || '',
      item.diagnosisCode5 || '',
      item.diagnosisCode6 || '',
      item.diagnosisCode7 || '',
      item.diagnosisCode8 || '',
      item.diagnosisCode9 || '',
      item.diagnosisCode10 || '',
      item.serviceUnitCost || '',
      item.serviceChargeAmounts || '',
      '1',
      item.invoiceDate || '',
      item.fin || '',
      item.referralNumber || '',
      item.claimFrequencyTypeCode || '',
      item.releaseOfInfoCode || ''
    ]);

    // Escape CSV values and wrap in quotes if they contain commas
    const escapeCSVValue = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvData.map((row: string[]) => row.map(escapeCSVValue).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename with current date
    const currentDate = new Date().toISOString().split('T')[0];
    const dateRange = startDate && endDate ? `_${startDate}_to_${endDate}` : '';
    link.setAttribute('download', `billing_events_${currentDate}${dateRange}.csv`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Downloaded ${data.listBillingEvents.length} billing events to CSV`);
  };
  
  // Handle filtering of data
  const filteredData = data?.listBillingEvents?.filter((item: BillingEvent) => {
    if (!searchTerm) return true;
    
    const searchTermLower = searchTerm.toLowerCase();
    return (
      (item.cin && item.cin.toLowerCase().includes(searchTermLower)) ||
      (item.mbrLastname && item.mbrLastname.toLowerCase().includes(searchTermLower)) ||
      (item.mbrFirstname && item.mbrFirstname.toLowerCase().includes(searchTermLower)) ||
      (item.providerName && item.providerName.toLowerCase().includes(searchTermLower))
    );
  }) || [];
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  
  // Show error notification if query fails
  useEffect(() => {
    if (error) {
      toast.error(`Error fetching billing events: ${error.message}`);
    }
  }, [error]);
  
  // Define core columns to display (you can add more as needed)
  const coreColumns: Column[] = [
    { key: 'cin', label: 'CIN' },
    { key: 'mbrLastname', label: 'Last Name' },
    { key: 'mbrFirstname', label: 'First Name' },
    { key: 'mbrDob', label: 'DOB' },
    { key: 'providerName', label: 'Provider' },
    { key: 'serviceStartDate', label: 'Service Start' },
    { key: 'serviceEndDate', label: 'Service End' },
    { key: 'procedureCode', label: 'Procedure Code' },
    { key: 'invoiceAmount', label: 'Invoice Amount' }
  ];
  
  // Handle page changes
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(prev => prev - 1);
    }
  };
  
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(prev => prev + 1);
    }
  };
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Billing Events</h1>
      
      {/* Search and filter */}
      <div className="mb-4">
        <div className="flex gap-4 flex-wrap">
          <div className="flex flex-col">
            <label htmlFor="billing-report-start-date" className="text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              id="billing-report-start-date"
              type="date"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0); // Reset to first page when filtering
              }}
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="billing-report-end-date" className="text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              id="billing-report-end-date"
              type="date"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0); // Reset to first page when filtering
              }}
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="billing-report-search" className="text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              id="billing-report-search"
              type="text"
              placeholder="Search by name, CIN, provider..."
              className="px-4 py-2 border border-gray-300 rounded-md w-full max-w-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0); // Reset to first page when searching
              }}
            />
          </div>
          <div className="flex flex-col justify-end">
            <div className="flex gap-2">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
              <button
                onClick={downloadCSV}
                disabled={!data?.listBillingEvents || data.listBillingEvents.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download CSV
              </button>
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setSearchTerm('');
                  setPage(0);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-2">Loading billing events...</span>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          Failed to load billing events. Please try again later.
        </div>
      )}
      
      {/* Initial state - no data loaded yet */}
      {!loading && !error && !data && (
        <div className="flex justify-center items-center h-64 text-gray-500">
          <div className="text-center">
            <p className="mb-2">No data loaded yet</p>
            <p className="text-sm">Click "Generate Report" to load billing events</p>
          </div>
        </div>
      )}
      
      {/* Data table */}
      {!loading && !error && data && (
        <>
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  {coreColumns.map((column) => (
                    <th 
                      key={column.key}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {column.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.length > 0 ? (
                  paginatedData.map((item: BillingEvent, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {page * rowsPerPage + index + 1}
                      </td>
                      {coreColumns.map((column) => (
                        <td key={`${index}-${column.key}`} className="px-4 py-3 whitespace-nowrap text-sm">
                          {column.key === 'serviceUnitCount' 
                            ? formatServiceUnitCount(item[column.key])
                            : column.key === 'invoiceAmount'
                            ? formatInvoiceAmount()
                            : (item[column.key] || '-')
                          }
                        </td>
                      ))}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        <button 
                          onClick={() => handleViewBillingEvent(item)}
                          className="text-blue-500 hover:text-blue-700 mr-2"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={coreColumns.length + 2} className="px-4 py-8 text-center text-gray-500">
                      No billing events found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              Showing {filteredData.length > 0 ? page * rowsPerPage + 1 : 0} to {Math.min((page + 1) * rowsPerPage, filteredData.length)} of {filteredData.length} entries
            </div>
            <div className="flex items-center space-x-2">
              <select 
                className="border rounded px-2 py-1 text-sm"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0); // Reset to first page when changing rows per page
                }}
              >
                {[10, 25, 50, 100, 250, 500, 1000, 2000].map(value => (
                  <option key={value} value={value}>
                    {value} per page
                  </option>
                ))}
              </select>
              
              <button
                onClick={handlePrevPage}
                disabled={page === 0}
                className={`px-3 py-1 rounded ${page === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
              >
                Previous
              </button>
              
              <span className="text-sm">
                Page {page + 1} of {totalPages || 1}
              </span>
              
              <button
                onClick={handleNextPage}
                disabled={page >= totalPages - 1 || totalPages === 0}
                className={`px-3 py-1 rounded ${page >= totalPages - 1 || totalPages === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Billing Event Details Modal */}
      {isModalOpen && selectedBillingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Billing Event Details</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Member Information */}
                <div className="space-y-4">
                  <h3 className="text-md font-medium text-gray-900 border-b pb-2">Member Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">CIN:</span>
                      <p className="text-gray-900">{selectedBillingEvent.cin || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">ORCHID MRUN:</span>
                      <p className="text-gray-900">{selectedBillingEvent.orchidMrun || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Last Name:</span>
                      <p className="text-gray-900">{selectedBillingEvent.mbrLastname || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">First Name:</span>
                      <p className="text-gray-900">{selectedBillingEvent.mbrFirstname || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Middle Name:</span>
                      <p className="text-gray-900">{selectedBillingEvent.mbrMiddlename || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">DOB:</span>
                      <p className="text-gray-900">{selectedBillingEvent.mbrDob || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Gender:</span>
                      <p className="text-gray-900">{selectedBillingEvent.mbrGender || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Homeless Status:</span>
                      <p className="text-gray-900">{selectedBillingEvent.memberHomelessStatus || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Provider Information */}
                <div className="space-y-4">
                  <h3 className="text-md font-medium text-gray-900 border-b pb-2">Provider Information</h3>
                  <div className="grid grid-cols-1 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Provider Name:</span>
                      <p className="text-gray-900">{selectedBillingEvent.providerName || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Provider TIN:</span>
                      <p className="text-gray-900">{selectedBillingEvent.providerTin || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Provider Phone:</span>
                      <p className="text-gray-900">{selectedBillingEvent.providerPhone || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Provider Address:</span>
                      <p className="text-gray-900">
                        {[
                          selectedBillingEvent.providerStreetAddress,
                          selectedBillingEvent.providerCity,
                          selectedBillingEvent.providerState,
                          selectedBillingEvent.providerZip
                        ].filter(Boolean).join(', ') || '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Service Information */}
                <div className="space-y-4">
                  <h3 className="text-md font-medium text-gray-900 border-b pb-2">Service Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Service Start Date:</span>
                      <p className="text-gray-900">{selectedBillingEvent.serviceStartDate || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Service End Date:</span>
                      <p className="text-gray-900">{selectedBillingEvent.serviceEndDate || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Procedure Code:</span>
                      <p className="text-gray-900">{selectedBillingEvent.procedureCode || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">POS Code:</span>
                      <p className="text-gray-900">{selectedBillingEvent.posCode || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Service Unit Count:</span>
                      <p className="text-gray-900">{formatServiceUnitCount(selectedBillingEvent.serviceUnitCount)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Service Unit Cost:</span>
                      <p className="text-gray-900">{selectedBillingEvent.serviceUnitCost || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Billing Information */}
                <div className="space-y-4">
                  <h3 className="text-md font-medium text-gray-900 border-b pb-2">Billing Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Invoice Amount:</span>
                      <p className="text-gray-900">{formatInvoiceAmount()}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Invoice Date:</span>
                      <p className="text-gray-900">{selectedBillingEvent.invoiceDate || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">FIN:</span>
                      <p className="text-gray-900">{selectedBillingEvent.fin || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Referral Number:</span>
                      <p className="text-gray-900">{selectedBillingEvent.referralNumber || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-gray-200">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
